import React, { useRef, useState } from "react";
import { useContext } from "react";
import { UserContext } from "../../features/UserPanel/userContext/UserContext";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Paper,
  Chip,
  LinearProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { ShieldOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "../../assets/logo.png"; // Ajuste o caminho conforme necessário
import {
  clearStoredAcquisition,
  getAcquisitionForRegister,
} from "../../utils/acquisitionTracking";
import { registrarSalesEvent } from "../../utils/salesEvents.js";
import {
  applyMetaAdvancedMatching,
  trackMetaEvent,
} from "../../utils/metaPixel";

function Register() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInFlightRef = useRef(false);
  const navigate = useNavigate();

  const steps = ["Dados Pessoais", "Contato", "Segurança"];

  const validatePhone = (phone) => /^(?:\+55)?\d{2}9\d{8}$/.test(phone);

  const validatePassword = (password) => ({
    length: password.length >= 6,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "phone") {
      setErrors((prev) => ({
        ...prev,
        phone: !value
          ? "Digite seu telefone."
          : !validatePhone(value)
          ? "Formato inválido. Use: 11999999999 ou +5511999999999"
          : "",
      }));
    }

    if (name === "password") {
      const rules = validatePassword(value);
      setErrors((prev) => ({
        ...prev,
        password: Object.values(rules).every(Boolean) ? "" : "A senha deve ter pelo menos 6 caracteres.",
      }));
    }
  };

  const validateStep = async () => {
    let newErrors = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = "Digite seu nome completo.";
      if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
        newErrors.email = "Digite um email válido.";
      } else {
        try {
          await axios.post(`${process.env.REACT_APP_API_BASE_URL}/auth/check-email`, {
            email: formData.email,
          });
        } catch (error) {
          if (error.response?.status === 400) {
            newErrors.email = "Este email já está em uso.";
          } else {
            toast.error("Erro ao verificar o email.");
            setErrors(newErrors);
            return false;
          }
        }
      }
    }

    if (step === 2) {
      if (!formData.phone) newErrors.phone = "Digite seu telefone.";
      else if (!validatePhone(formData.phone))
        newErrors.phone = "Formato inválido. Use: 11999999999 ou +5511999999999";
    }

    if (step === 3) {
      const passwordRules = validatePassword(formData.password);
      if (!Object.values(passwordRules).every(Boolean))
        newErrors.password = "A senha deve ter pelo menos 6 caracteres.";
      if (formData.password !== formData.confirmPassword)
        newErrors.confirmPassword = "As senhas não coincidem.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = async () => {
    if (await validateStep()) setStep(step + 1);
  };

  const handleBackStep = () => {
    if (step > 1) setStep(step - 1);
  };


  const { setUser } = useContext(UserContext);

const handleSubmit = async () => {
  if (submissionInFlightRef.current) return;

  submissionInFlightRef.current = true;
  setIsSubmitting(true);

  try {
    if (await validateStep()) {
      // 1. Cria o usuário
      const acquisition = getAcquisitionForRegister();
      const registerPayload = acquisition
        ? { ...formData, acquisition }
        : formData;

      const registerResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/register`,
        registerPayload
      );
      if (
        registerResponse.status === 201 &&
        registerResponse.data?.newUser?.id
      ) {
        const newUserId = registerResponse.data.newUser.id;
        const nameParts = String(formData.fullName || "")
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        applyMetaAdvancedMatching({
          email: formData.email,
          phone: formData.phone,
          firstName: nameParts[0],
          lastName: nameParts.length > 1 ? nameParts.at(-1) : "",
          externalId: newUserId,
        });
        trackMetaEvent(
          "CompleteRegistration",
          {
            content_name: "Cadastro Future Trade",
            content_ids: ["future-trade-annual"],
            content_type: "product",
            status: true,
            currency: "BRL",
          },
          `reg:${newUserId}`
        );
      }
      if (acquisition) {
        clearStoredAcquisition();
      }
      toast.success("Usuário criado com sucesso!");

      // 2. Faz login automático
      const loginResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password, // ou formData.phone
        }),
      });

      const data = await loginResponse.json();

      if (loginResponse.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("role", data.role);

        // ✔️ Atualiza contexto corretamente
        setUser({
          id: data.userId,
          email: formData.email,
          role: data.role,
          isPaid: data.isPaid,
        });

        registrarSalesEvent(data.userId, "user_login", {
          source: "register_auto_login",
          metadata: { path: "/register" },
        });

        // ✔️ Redireciona conforme papel
        navigate("/painel");
      } else {
        toast.error(data.message || "Erro ao fazer login.");
      }
    }
  } catch (error) {
    console.error("Erro ao registrar/logar:", error);
    toast.error("Erro ao registrar ou logar.");
  } finally {
    submissionInFlightRef.current = false;
    setIsSubmitting(false);
  }
};

  
  const renderPasswordRequirements = (password) => {
    const rules = validatePassword(password);
    const requirements = [
      { text: "Pelo menos 6 caracteres", met: rules.length },
    ];

    return (
      <Box sx={{ mt: 1, textAlign: "left" }}>
        {requirements.map((req, index) => (
          <Typography
            key={index}
            variant="caption"
            sx={{ color: req.met ? "#00ff00" : "#ff1744", display: "block" }}
          >
            {req.met ? "✓" : "✗"} {req.text}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 1:
        return (
          <Fade in={step === 1}>
            <Box>
              <TextField
                label="Nome Completo"
                name="fullName"
                fullWidth
                value={formData.fullName}
                onChange={handleChange}
                error={!!errors.fullName}
                helperText={errors.fullName}
                sx={{ mb: 2 }}
                inputProps={{ autoCapitalize: "words" }}
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                sx={{ mb: 2 }}
              />
            </Box>
          </Fade>
        );
      case 2:
        return (
          <Fade in={step === 2}>
            <Box>
              <TextField
                label="Telefone"
                name="phone"
                type="tel"
                fullWidth
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone || "Ex: 41992331209 ou +5541992331209"}
                sx={{ mb: 2 }}
              />
            </Box>
          </Fade>
        );
      case 3:
        return (
          <Fade in={step === 3}>
            <Box>
              <TextField
                label="Senha"
                type={showPassword ? "text" : "password"}
                name="password"
                fullWidth
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {renderPasswordRequirements(formData.password)}
              <TextField
                label="Confirmar Senha"
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                fullWidth
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                sx={{ mt: 2, mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Fade>
        );
      default:
        return null;
    }
  };

  return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at 15% 20%, rgba(52,84,140,0.35) 0%, rgba(7,12,20,1) 48%), radial-gradient(circle at 85% 10%, rgba(33,128,103,0.22) 0%, rgba(7,12,20,0) 45%), #070C14",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, md: 4 },
        }}
      >
        <Paper
          sx={{
            width: "100%",
            maxWidth: 460,
            p: { xs: 2.5, sm: 3.2 },
            borderRadius: 4,
            background: "linear-gradient(145deg, rgba(11,18,28,0.95) 0%, rgba(9,14,23,0.95) 100%)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 20px 48px rgba(0,0,0,0.35)",
            color: "#F5FAFF",
          }}
        >
          <Chip
            icon={<ShieldOutlined />}
            label="Cadastro seguro"
            sx={{
              mb: 1.5,
              color: "#CFE9FF",
              bgcolor: "rgba(73,149,255,0.18)",
              border: "1px solid rgba(73,149,255,0.35)",
              fontWeight: 700,
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.6 }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{ width: 42, height: 42, borderRadius: "50%" }}
            />
            <Typography sx={{ fontWeight: 900, fontSize: "1.45rem", color: "#F6FBFF" }}>
              Criar conta
            </Typography>
          </Box>
          <Typography sx={{ mb: 2.2, color: "rgba(231,240,250,0.72)" }}>
            Leva menos de 2 minutos para começar.
          </Typography>

          <LinearProgress
            variant="determinate"
            value={(step / 3) * 100}
            sx={{
              mb: 1.2,
              height: 7,
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.09)",
              "& .MuiLinearProgress-bar": { bgcolor: "#4E9AFD" },
            }}
          />
          <Typography sx={{ mb: 2, fontSize: "0.85rem", color: "rgba(231,240,250,0.7)" }}>
            Etapa {step} de 3: {steps[step - 1]}
          </Typography>

          <Stepper
            activeStep={step - 1}
            sx={{ mb: 3, display: { xs: "none", sm: "flex" } }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      color: "rgba(231,240,250,0.74)",
                      "&.Mui-active": { color: "#9CC8FF" },
                      "&.Mui-completed": { color: "#63D89A" },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(step)}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            {step > 1 && (
              <Button
                onClick={handleBackStep}
                variant="outlined"
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  borderColor: "rgba(255,255,255,0.25)",
                  color: "rgba(231,240,250,0.88)",
                }}
              >
                Voltar
              </Button>
            )}
            <Button
              onClick={step === 3 ? handleSubmit : handleNextStep}
              variant="contained"
              disabled={
                (step === 2 && !!errors.phone) ||
                (step === 3 && (!!errors.password || isSubmitting))
              }
              sx={{
                ml: "auto",
                px: 3,
                py: 1.1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 800,
                background: "linear-gradient(90deg, #2E78E6 0%, #4E9AFD 100%)",
                "&:hover": { background: "linear-gradient(90deg, #3A85F0 0%, #62A8FF 100%)" },
              }}
            >
              {step === 3 ? "Finalizar" : "Avançar"}
            </Button>
          </Box>


        {/* Botão para usuários com cadastro existente */}
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button
            onClick={() => navigate("/login")}
            variant="text"
            sx={{
              color: "#9CC8FF",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "rgba(73,149,255,0.12)",
              },
            }}
          >
            Já possui cadastro? Clique aqui
          </Button>
        </Box>
        </Paper>
      </Box>    
  );
}

export default Register;
